import { useState, useEffect } from 'react';

interface Step {
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface ProcessTimelineProps {
  title: React.ReactNode;
  subtitle: string;
  steps: Step[];
}

export default function ProcessTimeline({ title, subtitle, steps }: ProcessTimelineProps) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="w-full">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          {title}
        </h2>
        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
          {subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 relative">
        <div className="hidden lg:block absolute top-8 left-0 right-0 h-0.5 bg-gray-700 transform -translate-y-1/2 z-0"></div>
        
        {steps.map((step, index) => (
          <div
            key={index}
            className={`relative group cursor-pointer transition-all duration-500`}
            onMouseEnter={() => setActiveStep(index)}
          >
            <div className="flex flex-col items-center text-center relative z-10">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-all duration-300 ${
                activeStep === index 
                  ? `bg-gradient-to-r ${step.color} scale-110 shadow-lg` 
                  : 'bg-gray-800 border border-gray-700'
              }`}>
                <i className={`${step.icon} text-xl text-white`}></i>
              </div>
              
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:border-gray-500 w-full h-full min-h-[180px]">
                <h3 className={`text-lg font-bold mb-3 transition-colors duration-300 ${
                  activeStep === index ? 'text-white' : 'text-gray-300'
                }`}>
                  {step.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}